/**
 * WebGL2 device-capability probe + residency-policy HARD GATE (Phase 5).
 *
 * Reads the three limits the texture design depends on, attempts a real
 * probe-allocation of one N-layer 600x800 R8 TEXTURE_2D_ARRAY (N = the dataset's
 * time-slice count, read from the manifest — 52 weekly slices in the current
 * build), estimates a VRAM budget, and SELECTS the residency / texture-layout
 * policy:
 *   - "full-selected" (primary build target): keep every selected allergen's
 *     36-layer array fully resident.
 *   - "lru-capped": VRAM-constrained device; cap concurrently-resident allergen
 *     arrays to an LRU budget (per-allergen arrays still uploaded whole, so
 *     scrub stays hitch-free — only allergen *switching* may cost one upload).
 *   - "grid-atlas": freak device with too few fragment texture units for the
 *     per-allergen multi-binding path. Out of scope for this spike build (see
 *     RED-TEAM note below) — selected only to produce a clear error.
 *
 * Guaranteed WebGL2 floors we rely on (plan section 0.1):
 *   MAX_TEXTURE_SIZE          >= 2048   (600x800 layer safe everywhere)
 *   MAX_ARRAY_TEXTURE_LAYERS  >= 256    (52-layer array always safe)
 *   MAX_TEXTURE_IMAGE_UNITS   >= 16     (up to 12 sampler2DArray + mask)
 *
 * RED-TEAM cleanup (redteam-plan-v2): the grid-atlas fallback trigger is
 * MAX_TEXTURE_IMAGE_UNITS < 14, NOT < 13. Worst case = 12 selected allergen
 * arrays + 1 shared no-data/ocean mask = 13 samplers, so >= 14 units are
 * required to stay on the per-allergen path. (A live probe on this machine
 * reported 32 units, so the per-allergen path is the build target.)
 */

export type ResidencyPolicy = "full-selected" | "lru-capped" | "grid-atlas";

export interface DeviceCaps {
  webgl2: boolean;
  maxTextureSize: number;
  maxArrayTextureLayers: number;
  maxTextureImageUnits: number;
  maxCombinedTextureImageUnits: number;
  /** navigator.deviceMemory (GiB) UA hint, if available. */
  deviceMemoryGiB: number | null;
  renderer: string | null;
  vendor: string | null;
  /** Did a real probe allocation of an N-layer 600x800 R8 array succeed? */
  arrayAllocOk: boolean;
  /** The time-slice count the probe allocated for (manifest-driven, e.g. 52). */
  probedSlices: number;
}

/** Result of the hard gate: which layout/residency the app will use. */
export interface CapsDecision {
  caps: DeviceCaps;
  /** True iff the per-allergen array layout is usable on this device. */
  supported: boolean;
  policy: ResidencyPolicy;
  /** Estimated max number of concurrently-resident allergen arrays. */
  maxResidentAllergens: number;
  /** Human-readable explanation (logged + shown to the user on failure). */
  reason: string;
}

/**
 * The number of time-slices per allergen array. Read DYNAMICALLY from the
 * manifest (manifest.timeSlices.length / nTimeSlices) and passed into probeCaps
 * + decide so the probe allocation, the MAX_ARRAY_TEXTURE_LAYERS floor and the
 * per-allergen VRAM math all track the dataset (52 weekly slices in the current
 * build; was 36 dekadal). Default kept at 52 to match the shipped dataset.
 */
export const DEFAULT_SLICES = 52;

/** WebGL2 floors that the per-allergen N-layer 600x800 R8 layout requires. */
export function requiredFloors(nSlices: number = DEFAULT_SLICES) {
  return {
    maxTextureSize: 800, // largest single layer dimension we ever create
    maxArrayTextureLayers: nSlices, // N weekly slices per allergen array
    // RED-TEAM: 12 allergen arrays + 1 shared mask = 13 samplers -> need >= 14.
    maxTextureImageUnits: 14,
  } as const;
}

/** Back-compat constant export (uses the default slice count). */
export const REQUIRED_FLOORS = requiredFloors(DEFAULT_SLICES);

const N_ALLERGENS = 12;
/**
 * Per-allergen GPU footprint: R8 (w*h) * nSlices (default 600x800 @ 52 weekly ->
 * ~24.96 MB). The default `(600,800)` keeps every EXISTING single-region call
 * byte-identical (EU's grid is exactly 600x800); the multi-region budget
 * (decideMultiRegion) passes each region's REAL dims so the SUM across regions
 * of differing grids is correct instead of pretending every region is 600x800.
 */
function perAllergenVramBytes(nSlices: number, w = 600, h = 800): number {
  return nSlices * w * h;
}

/**
 * TEST-ONLY low-cap hook. The harness's `navigator.deviceMemory` override can
 * only push the budget floor (caps.ts `max(0.25 GiB, mem*0.15)`) down to a cap
 * of ~10; exercising the eviction path at a deeper cap (the plan's 4-6) needs an
 * explicit ceiling. When set (e.g. via `?capCeil=N` -> `setCapCeilForTest(N)`
 * from main.ts), BOTH decide() and decideMultiRegion() clamp their computed
 * `maxResidentAllergens` to <= this value, forcing LRU eviction for proofs. It
 * is `undefined` in all normal runs (zero product effect). Never set in prod.
 */
let CAP_CEIL_FOR_TEST: number | undefined;
/** Set/clear the test-only residency ceiling (see CAP_CEIL_FOR_TEST). */
export function setCapCeilForTest(n: number | undefined): void {
  CAP_CEIL_FOR_TEST =
    typeof n === "number" && Number.isFinite(n) && n >= 1
      ? Math.floor(n)
      : undefined;
}

/**
 * Read the WebGL2 limits and attempt a real N-layer array allocation.
 *
 * `probeW`/`probeH` default to 600x800 (the original probe) so the existing
 * single-region call site is byte-identical. A multi-region caller (A4) MAY pass
 * the LARGEST region grid across all regions so a device that can hold 600x800
 * but not e.g. 1014x1048 fails the gate cleanly here instead of OOMing at
 * runtime. For the current 3-region load the largest grid IS 600x800 (EU), so
 * passing it leaves the probe identical.
 */
export function probeCaps(
  nSlices: number = DEFAULT_SLICES,
  probeW = 600,
  probeH = 800,
): DeviceCaps {
  const canvas = document.createElement("canvas");
  const gl = canvas.getContext("webgl2");

  if (!gl) {
    return {
      webgl2: false,
      maxTextureSize: 0,
      maxArrayTextureLayers: 0,
      maxTextureImageUnits: 0,
      maxCombinedTextureImageUnits: 0,
      deviceMemoryGiB: deviceMemory(),
      renderer: null,
      vendor: null,
      arrayAllocOk: false,
      probedSlices: nSlices,
    };
  }

  let renderer: string | null = null;
  let vendor: string | null = null;
  const dbg = gl.getExtension("WEBGL_debug_renderer_info");
  if (dbg) {
    renderer = gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL) as string;
    vendor = gl.getParameter(dbg.UNMASKED_VENDOR_WEBGL) as string;
  }

  // Real probe allocation of one N-layer R8 array at the probe grid (default
  // 600x800 = the build target; the largest region grid when A4 passes it).
  let arrayAllocOk = false;
  try {
    const tex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D_ARRAY, tex);
    gl.texStorage3D(gl.TEXTURE_2D_ARRAY, 1, gl.R8, probeW, probeH, nSlices);
    arrayAllocOk = gl.getError() === gl.NO_ERROR;
    gl.deleteTexture(tex);
  } catch {
    arrayAllocOk = false;
  }

  const caps: DeviceCaps = {
    webgl2: true,
    maxTextureSize: gl.getParameter(gl.MAX_TEXTURE_SIZE) as number,
    maxArrayTextureLayers: gl.getParameter(
      gl.MAX_ARRAY_TEXTURE_LAYERS,
    ) as number,
    maxTextureImageUnits: gl.getParameter(gl.MAX_TEXTURE_IMAGE_UNITS) as number,
    maxCombinedTextureImageUnits: gl.getParameter(
      gl.MAX_COMBINED_TEXTURE_IMAGE_UNITS,
    ) as number,
    deviceMemoryGiB: deviceMemory(),
    renderer,
    vendor,
    arrayAllocOk,
    probedSlices: nSlices,
  };

  gl.getExtension("WEBGL_lose_context")?.loseContext();
  return caps;
}

/** True iff every required floor is met (sanity check; full gate is decide()). */
export function meetsFloors(
  caps: DeviceCaps,
  nSlices: number = DEFAULT_SLICES,
): boolean {
  const floors = requiredFloors(nSlices);
  return (
    caps.webgl2 &&
    caps.maxTextureSize >= floors.maxTextureSize &&
    caps.maxArrayTextureLayers >= floors.maxArrayTextureLayers &&
    caps.maxTextureImageUnits >= floors.maxTextureImageUnits
  );
}

/**
 * HARD GATE: probe + select the texture layout / residency policy.
 * `nSlices` is the manifest-driven time-slice count (52 weekly in this build);
 * it drives the array-layers floor and the per-allergen VRAM math.
 * Returns a CapsDecision whose `supported` flag the app uses to decide whether
 * to run (per-allergen path) or show a clear unsupported-device error.
 */
export function decide(
  caps: DeviceCaps,
  nSlices: number = DEFAULT_SLICES,
): CapsDecision {
  const floors = requiredFloors(nSlices);
  if (!caps.webgl2) {
    return {
      caps,
      supported: false,
      policy: "grid-atlas",
      maxResidentAllergens: 0,
      reason: "WebGL2 is not available in this browser.",
    };
  }
  if (caps.maxTextureSize < floors.maxTextureSize) {
    return {
      caps,
      supported: false,
      policy: "grid-atlas",
      maxResidentAllergens: 0,
      reason: `MAX_TEXTURE_SIZE ${caps.maxTextureSize} < ${floors.maxTextureSize}.`,
    };
  }
  if (caps.maxArrayTextureLayers < floors.maxArrayTextureLayers) {
    return {
      caps,
      supported: false,
      policy: "grid-atlas",
      maxResidentAllergens: 0,
      reason: `MAX_ARRAY_TEXTURE_LAYERS ${caps.maxArrayTextureLayers} < ${nSlices}.`,
    };
  }
  if (!caps.arrayAllocOk) {
    return {
      caps,
      supported: false,
      policy: "grid-atlas",
      maxResidentAllergens: 0,
      reason: `Probe allocation of a ${nSlices}-layer 600x800 R8 array failed.`,
    };
  }
  // RED-TEAM threshold: need >= 14 units (12 allergens + 1 mask = 13 samplers).
  if (caps.maxTextureImageUnits < floors.maxTextureImageUnits) {
    // The grid-atlas single-sampler fallback is explicitly OUT OF SCOPE for
    // this spike build (it requires a separate atlas encoding + shader path).
    // We surface a clear error rather than silently mis-render.
    return {
      caps,
      supported: false,
      policy: "grid-atlas",
      maxResidentAllergens: 0,
      reason:
        `MAX_TEXTURE_IMAGE_UNITS ${caps.maxTextureImageUnits} < 14: the ` +
        `per-allergen multi-binding path needs 12 allergen samplers + 1 mask. ` +
        `The grid-atlas fallback is out of scope for this build.`,
    };
  }

  // VRAM budget estimate. deviceMemory is a coarse hint (GiB of system RAM).
  // We reserve generously for the basemap, CPU-decoded copies, and the OS.
  // If we cannot confidently hold all 12 selected arrays, cap with an LRU.
  const memGiB = caps.deviceMemoryGiB ?? 4; // unknown -> assume a modest 4 GiB
  const vramBudgetBytes = Math.max(0.25, memGiB * 0.15) * 1024 * 1024 * 1024;
  const perAllergen = perAllergenVramBytes(nSlices); // ~24.96 MB at 52 slices
  const budgetFit = vramBudgetBytes >= perAllergen * N_ALLERGENS;
  let maxResident = budgetFit
    ? N_ALLERGENS
    : Math.max(
        1,
        Math.min(N_ALLERGENS, Math.floor(vramBudgetBytes / perAllergen)),
      );
  // TEST-ONLY ceiling (no effect unless setCapCeilForTest() was called).
  if (CAP_CEIL_FOR_TEST !== undefined) {
    maxResident = Math.max(1, Math.min(maxResident, CAP_CEIL_FOR_TEST));
  }
  const fitAll = maxResident >= N_ALLERGENS;

  return {
    caps,
    supported: true,
    policy: fitAll ? "full-selected" : "lru-capped",
    maxResidentAllergens: maxResident,
    reason: fitAll
      ? `Per-allergen path: all ${N_ALLERGENS} allergen arrays may be resident.`
      : `Per-allergen path (LRU-capped): up to ${maxResident} resident arrays ` +
        `(~${memGiB} GiB device).`,
  };
}

/** One region's grid for the multi-region VRAM budget. */
export interface RegionGrid {
  /** Time-slice count (52 weekly). */
  nSlices: number;
  /** Per-slice width in px (region grid, NOT a hardcoded 600). */
  w: number;
  /** Per-slice height in px (region grid, NOT a hardcoded 800). */
  h: number;
  /**
   * How many taxa this region actually carries. Caps the per-region resident
   * contribution at `min(k, regionTaxaCount)` so a small-taxa region never
   * inflates the budget beyond what it can hold. Defaults to N_ALLERGENS (12)
   * when unknown (the conservative choice — assumes the region could hold 12).
   */
  taxaCount?: number;
  /**
   * Change A — non-regression flag (the user's #1 hard rule). A `baseline:true`
   * region is one of the historically-shipped regions (EU/NA/East-Asia) whose
   * residency cap MUST equal the old single-region `decide()` answer at EVERY
   * device-memory value. Baseline regions are NEVER tightened by the presence of
   * other regions: device-memory pressure from ADDED (`baseline` falsey) regions
   * is absorbed by capping/evicting the ADDED regions FIRST. Defaults to `false`
   * (a newly-added region) so a future region must OPT IN to baseline protection.
   */
  baseline?: boolean;
}

/** decideMultiRegion's result: the single global cap + a per-region breakdown. */
export type MultiRegionDecision = CapsDecision & {
  /**
   * The cap the historical baseline regions receive — IDENTICAL to the old
   * single-region `decide()` answer on the same device. Also surfaced as
   * `maxResidentAllergens` so `__pollenDecision`/`setMaxResident` are unchanged
   * for the existing-only load.
   */
  globalMaxResident: number;
  /**
   * The per-region residency cap, aligned to the input `regions` order. Baseline
   * regions always get `globalMaxResident`; added regions get the (possibly
   * smaller) cap the REMAINING budget allows after the baseline footprint is
   * reserved. The app applies this per-region so adding regions can never lower
   * a baseline region's cap.
   */
  perRegionCap: number[];
  /** Resident arrays each region would hold at its per-region cap (diagnostic). */
  perRegionResident: number[];
  /** Σ over regions of (perRegionResident · nSlices · w · h) at the caps. */
  estimatedVramBytes: number;
};

/**
 * MULTI-REGION HARD GATE (plan §1.2 / §4.2 — Change A). Same budget BASIS as
 * decide() (same vramBudgetBytes formula, same floor checks via decide()), but
 * VRAM-aware of the REAL per-region grids — WITHOUT EVER degrading the
 * historically-shipped baseline regions.
 *
 * NON-REGRESSION PRINCIPLE (the user's #1 hard rule, F1): for the existing
 * region configuration (EU/NA/East-Asia, all `baseline:true`) the decision MUST
 * equal the old single-region `decide(caps,52)` at EVERY device-memory value
 * (0.5, 1, 2, 4, 8, undefined). Device-memory pressure introduced by ADDING new
 * regions is absorbed by capping/evicting the NEWLY-ADDED regions FIRST — it
 * NEVER lowers a baseline region's cap. Concretely:
 *
 *   1. The BASELINE cap is exactly `decide(caps, nSlices).maxResidentAllergens`
 *      (the historical single-region answer). Baseline regions ALWAYS receive
 *      this cap, regardless of how many other regions are present. ⇒ For the
 *      existing-only load `decideMultiRegion === decide` at every memory tier,
 *      by construction (verified arithmetically + by the E0 behavioral sweep).
 *   2. The baseline regions' REAL summed footprint at the baseline cap is then
 *      RESERVED out of the budget. The ADDED regions get the largest `k<=12`
 *      whose summed REAL footprint fits the REMAINING budget (floor 1 — graceful
 *      LRU, never a wrong pixel). Added regions are the only ones that tighten.
 *   3. `maxResidentAllergens` / `globalMaxResident` = the baseline cap, so
 *      `__pollenDecision` + `setMaxResident` are byte-identical for the
 *      existing-only load. `perRegionCap` carries the (possibly smaller) added-
 *      region caps; the app applies it PER region (main.ts), so a baseline
 *      region can never inherit an added region's lower cap.
 *   4. `policy` mirrors decide()'s policy when there are no added regions; with
 *      added regions present it is "full-selected" only if BOTH the baseline cap
 *      is 12 AND every added region also fits 12.
 *
 * The floor checks (webgl2 / texture size / array layers / alloc / texture
 * units) are delegated to decide() on the LARGEST region's slice count so an
 * unsupported device fails cleanly and identically.
 */
export function decideMultiRegion(
  caps: DeviceCaps,
  regions: RegionGrid[],
): MultiRegionDecision {
  // Use the LARGEST slice count as the floor representative so the floor checks
  // (and the reason text) reflect the heaviest array the device must allocate.
  // Empty input falls back to decide() with the default grid (defensive; the app
  // always has >=1 region).
  const nSlicesMax = regions.reduce((m, r) => Math.max(m, r.nSlices), 0);
  const baseSlices = nSlicesMax > 0 ? nSlicesMax : DEFAULT_SLICES;
  const base = decide(caps, baseSlices);
  // If the device is unsupported, surface that decision verbatim (no residency).
  if (!base.supported) {
    return {
      ...base,
      globalMaxResident: 0,
      perRegionCap: regions.map(() => 0),
      perRegionResident: regions.map(() => 0),
      estimatedVramBytes: 0,
    };
  }

  const memGiB = caps.deviceMemoryGiB ?? 4;
  const vramBudgetBytes = Math.max(0.25, memGiB * 0.15) * 1024 * 1024 * 1024;

  // (1) BASELINE cap = the historical single-region answer (already test-ceiled
  // inside decide()). This is what EU/NA/East-Asia receive at EVERY memory tier,
  // so for the existing-only load the result equals decide() exactly.
  const baselineCap = base.maxResidentAllergens;

  // (2) Reserve the baseline regions' REAL summed footprint at the baseline cap,
  // then fit the ADDED regions into whatever budget is left. Reserving the REAL
  // sum (not a 600x800 stand-in) only makes the ADDED-region fit more
  // conservative; it never touches a baseline region's own cap.
  const isAdded = (r: RegionGrid): boolean => r.baseline !== true;
  const footprintAt = (r: RegionGrid, cap: number): number =>
    Math.min(cap, r.taxaCount ?? N_ALLERGENS) *
    perAllergenVramBytes(r.nSlices, r.w, r.h);
  const baselineReserved = regions
    .filter((r) => !isAdded(r))
    .reduce((sum, r) => sum + footprintAt(r, baselineCap), 0);
  const addedBudget = Math.max(0, vramBudgetBytes - baselineReserved);
  const addedRegions = regions.filter(isAdded);
  const addedSumAt = (k: number): number =>
    addedRegions.reduce((sum, r) => sum + footprintAt(r, k), 0);

  // Largest k in [1..12] whose ADDED-region summed footprint fits the remaining
  // budget. Greedy from 12 down. Floor at 1 so the app always renders (LRU churns
  // gracefully — never a wrong pixel — if even k=1 over-commits a tiny budget).
  let addedCap = N_ALLERGENS;
  while (addedCap > 1 && addedSumAt(addedCap) > addedBudget) addedCap--;
  // TEST-ONLY ceiling (no effect unless setCapCeilForTest() was called). Applies
  // to the added-region cap only; the baseline cap was already ceiled in decide()
  // so the existing-only load stays byte-identical to decide() under the hook.
  if (CAP_CEIL_FOR_TEST !== undefined) {
    addedCap = Math.max(1, Math.min(addedCap, CAP_CEIL_FOR_TEST));
  }

  const perRegionCap = regions.map((r) => (isAdded(r) ? addedCap : baselineCap));
  const perRegionResident = regions.map((r, i) =>
    Math.min(perRegionCap[i], r.taxaCount ?? N_ALLERGENS),
  );
  const estimatedVramBytes = regions.reduce(
    (sum, r, i) => sum + footprintAt(r, perRegionCap[i]),
    0,
  );

  // policy mirrors decide() when there are no added regions; with added regions
  // it is full-selected only if BOTH the baseline cap is 12 AND every added
  // region fits 12.
  const addedFitAll =
    addedRegions.length === 0 || addedSumAt(N_ALLERGENS) <= addedBudget;
  const fitAll =
    base.policy === "full-selected" && baselineCap >= N_ALLERGENS && addedFitAll;

  return {
    caps,
    supported: true,
    policy: fitAll ? "full-selected" : "lru-capped",
    maxResidentAllergens: baselineCap,
    globalMaxResident: baselineCap,
    perRegionCap,
    perRegionResident,
    estimatedVramBytes,
    reason:
      addedRegions.length === 0
        ? // No added regions: the decision IS decide() — reuse its exact reason.
          base.reason
        : fitAll
          ? `Per-allergen path: all ${N_ALLERGENS} allergen arrays may be ` +
            `resident across ${regions.length} region(s).`
          : `Per-allergen path: baseline regions hold ${baselineCap}, added ` +
            `regions LRU-capped to ${addedCap} (~${memGiB} GiB device, ` +
            `${addedRegions.length} added region(s)).`,
  };
}

function deviceMemory(): number | null {
  const nav = navigator as Navigator & { deviceMemory?: number };
  return typeof nav.deviceMemory === "number" ? nav.deviceMemory : null;
}
